import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { childrenApi } from '../../services/api';
import { queryKeys } from '../../lib/queryClient';
import { useToast } from '../../components/ui';
import { extractApiErrorMessage } from '../../utils/apiError';
import { ChildCreate, ChildUpdate, ChildFilters } from '../../types/api';

/**
 * Hook to fetch children with filters
 */
export const useChildren = (filters?: ChildFilters) => {
  return useQuery({
    queryKey: queryKeys.children(filters),
    queryFn: () => childrenApi.getChildren(filters),
    placeholderData: (previousData) => previousData, // Keep previous data while fetching new page
  });
};

/**
 * Hook to fetch a specific child
 */
export const useChild = (childId: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.child(childId),
    queryFn: () => childrenApi.getChild(childId),
    enabled: enabled && !!childId,
  });
};

/**
 * Hook to create a new child
 */
export const useCreateChild = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  
  return useMutation({
    mutationFn: (data: ChildCreate) => childrenApi.createChild(data),
    onSuccess: (data) => {
      // Invalidate children list
      queryClient.invalidateQueries({ queryKey: queryKeys.children() });
      
      // Show success message
      addToast({
        title: 'Child created',
        message: `${data.name} ${data.surname} has been added successfully`,
        type: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        title: 'Failed to create child',
        message: extractApiErrorMessage(error, 'An error occurred'),
        type: 'error',
      });
    },
  });
};

/**
 * Hook to update a child
 */
export const useUpdateChild = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  
  return useMutation({
    mutationFn: ({ childId, data }: { childId: string; data: ChildUpdate }) => 
      childrenApi.updateChild(childId, data),
    onSuccess: (data, variables) => {
      // Update cache for specific child
      queryClient.setQueryData(queryKeys.child(variables.childId), data);
      
      // Invalidate children list
      queryClient.invalidateQueries({ queryKey: queryKeys.children() });
      
      // Show success message
      addToast({
        title: 'Child updated',
        message: `${data.name} ${data.surname} has been updated successfully`,
        type: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        title: 'Failed to update child',
        message: extractApiErrorMessage(error, 'An error occurred'),
        type: 'error',
      });
    },
  });
};

/**
 * Hook to delete a child
 */
export const useDeleteChild = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  
  return useMutation({
    mutationFn: (childId: string) => childrenApi.deleteChild(childId),
    onSuccess: (_, childId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.child(childId) });
      
      // Invalidate children list
      queryClient.invalidateQueries({ queryKey: queryKeys.children() });
      
      // Show success message
      addToast({
        title: 'Child deleted',
        message: 'The child has been removed successfully',
        type: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        title: 'Failed to delete child',
        message: extractApiErrorMessage(error, 'An error occurred'),
        type: 'error',
      });
    },
  });
};

/**
 * Hook to fetch children by classroom
 */
export const useChildrenByClassroom = (classroomId: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.childrenByClassroom(classroomId),
    queryFn: () => childrenApi.getChildrenByClassroom(classroomId),
    enabled: enabled && !!classroomId,
  });
};

/**
 * Hook to fetch children by guardian
 */
export const useChildrenByGuardian = (guardianId: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.childrenByGuardian(guardianId),
    queryFn: () => childrenApi.getChildrenByGuardian(guardianId),
    enabled: enabled && !!guardianId,
  });
};
